import React, { useState, useEffect, useRef } from 'react';
import { Check, Copy, Loader2, Send, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import { formatGroupedNumber } from 'lib/dateFormat';
import config from 'config';
import {
  createPortalCheckout,
  getPortalPaymentDetails,
  getPortalPaymentMethods,
  type PortalPaymentDetails,
  type PortalSubscription,
} from 'modules/portal-plans/api';
import { PAYMENT_SUPPORT_TELEGRAM, isOnlineMethod, pickPaymentMethod, type PaymentMethod } from 'modules/payments/methods';
import { PaymentMethodMark } from 'components/payments/PaymentMethodMark';
import { CYCLE_LABELS, cycleOffer, offeredCycles, type CycleOffer, type PortalCycle } from 'modules/portal-plans/cycles';

export interface PlanCheckoutModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPlan?: 'START' | 'PRO' | 'PREMIUM';
  onboardingOffer?: boolean;
  defaultPaymentMethod?: PaymentMethod;
  subscription?: PortalSubscription | null;
}

export interface PortalPlanItem {
  id: 'START' | 'PRO' | 'PREMIUM';
  name: string;
  originalPrice: number;
  discountPrice: number;
  dailyPrice: number;
  mockLimit?: number;
  analysisLimit?: number;
  /** 3-month and 7-day prices; absent or 0 when not sold. */
  quarterlyPrice?: number;
  weeklyPrice?: number;
  weeklyAnalysisLimit?: number;
  subtitle: string;
  features: string[];
  isRecommended?: boolean;
}

export function buildPortalFeaturesList(plan: {
  mockLimit?: number;
  analysisLimit?: number;
  hasAnswerAnalysis?: boolean;
  hasStudyPlan?: boolean;
  hasTeacherConsultation?: boolean;
  hasPersonalAnalytics?: boolean;
  features?: string[];
}): string[] {
  const list: string[] = [];
  if (plan.mockLimit) list.push(`${plan.mockLimit} ta Full Mock`);
  if (plan.analysisLimit) list.push(`IELTS/SAT: oyiga ${plan.analysisLimit} ta to'liq tahlil`);
  if (plan.hasAnswerAnalysis) list.push('Javoblar tahlili');
  if (plan.hasStudyPlan) list.push('30 kunlik tayyorgarlik rejasi');
  if (plan.hasTeacherConsultation) list.push('Ustoz bilan suhbat');
  if (plan.hasPersonalAnalytics) list.push('Shaxsiy natijalar tahlili');
  return list.length > 0 ? list : plan.features || [];
}

export const PORTAL_PLANS: PortalPlanItem[] = [
  {
    id: 'START',
    name: 'START',
    originalPrice: 59000,
    discountPrice: 49000,
    dailyPrice: 1633,
    mockLimit: 30,
    analysisLimit: 0,
    subtitle: 'Boshlash uchun kerakli asosiy imkoniyatlar',
    features: ['30 ta Full Mock', '30 kunlik tayyorgarlik rejasi'],
  },
  {
    id: 'PRO',
    name: 'PRO',
    originalPrice: 129000,
    discountPrice: 99000,
    dailyPrice: 3300,
    mockLimit: 60,
    analysisLimit: 10,
    quarterlyPrice: 249000,
    weeklyPrice: 39000,
    weeklyAnalysisLimit: 5,
    isRecommended: true,
    subtitle: 'Eng maqbul tanlov',
    features: [
      '60 ta Full Mock',
      "IELTS/SAT: oyiga 10 ta to'liq tahlil",
      'Javoblar tahlili',
      '30 kunlik tayyorgarlik rejasi',
      'Shaxsiy natijalar tahlili',
    ],
  },
  {
    id: 'PREMIUM',
    name: 'PREMIUM',
    originalPrice: 249000,
    discountPrice: 199000,
    dailyPrice: 6633,
    mockLimit: 150,
    analysisLimit: 30,
    quarterlyPrice: 499000,
    subtitle: "Ko'proq mashq. Kuchliroq tayyorgarlik.",
    features: [
      '150 ta Full Mock',
      "IELTS/SAT: oyiga 30 ta to'liq tahlil",
      'Javoblar tahlili',
      '30 kunlik tayyorgarlik rejasi',
      'Ustoz bilan suhbat',
      'Shaxsiy natijalar tahlili',
    ],
  },
];

const DEFAULT_PLAN_RANKS: Record<string, number> = {
  START: 1,
  PRO: 2,
  PREMIUM: 3,
};

function getPlanRank(id: string): number {
  return DEFAULT_PLAN_RANKS[id.toUpperCase()] ?? 0;
}

type PlanId = PortalPlanItem['id'];

/** "63 750 so'm": so'm amounts are grouped with a space, as everywhere else in the app. */
const som = (amount: number): string => `${formatGroupedNumber(amount)} so'm`;

/** Seeded plan names are upper-case keys ("PREMIUM"); the dialog reads them as words ("Premium"). */
const planLabel = (name: string): string =>
  name === name.toUpperCase() ? name.charAt(0) + name.slice(1).toLowerCase() : name;

/** The one-line summary under a plan's name: its mock allowance for the period, else its subtitle. */
const planSummary = (plan: PortalPlanItem, offer?: CycleOffer): string => {
  const mocks = offer ? offer.mockLimit : plan.mockLimit;
  return mocks ? `${mocks} ta Full Mock` : plan.subtitle;
};

/** The monthly feature lines, with the test and analysis counts of a 3-month plan or 7-day pass. */
function periodFeatures(plan: PortalPlanItem, cycle: PortalCycle, offer: CycleOffer): string[] {
  if (cycle === 'monthly') return plan.features;
  return plan.features.flatMap((feature) => {
    if (plan.mockLimit && feature === `${plan.mockLimit} ta Full Mock`) return [`${offer.mockLimit} ta Full Mock`];
    if (feature.startsWith('IELTS/SAT: oyiga')) return offer.analysisLimit ? [`IELTS/SAT: ${offer.analysisLimit} ta to'liq tahlil`] : [];
    return [feature];
  });
}

const CARD_TRANSFER_LABEL = 'Karta orqali';
const METHOD_NAMES: Record<PaymentMethod, string> = {
  payme: 'Payme', click: 'Click', uzum: 'Uzum', uzcard: 'Uzcard', humo: 'Humo', visa_mc: 'Visa / Mastercard', card: CARD_TRANSFER_LABEL,
};
const methodHint = (method: PaymentMethod): string =>
  method === 'card' ? 'Chek bilan · 2–5\u00a0daqiqa' : 'Darhol faollashadi';

/** A choice card: the chosen one is outlined in ink, never lime (lime is the pay button). */
const CHOICE =
  'rounded-lg border bg-card transition-[border-color,box-shadow] duration-150';
const CHOICE_ON = 'border-foreground ring-1 ring-foreground';
const CHOICE_OFF = 'border-border hover:border-foreground/30';
const CHOICE_FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60';

/** An 18px ink radio that fills to a 5px ring when chosen, as in the exam. */
function RadioMark({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'mt-px h-[18px] w-[18px] shrink-0 rounded-full transition-[border-width,border-color] duration-150',
        checked ? 'border-[5px] border-foreground' : 'border-[1.5px] border-foreground/25',
      )}
    />
  );
}

/** Arrow keys move the choice inside a radiogroup and keep focus on the chosen radio. */
function arrowKeys<T extends string>(values: T[], current: T, select: (value: T) => void, idOf: (value: T) => string) {
  return (e: React.KeyboardEvent) => {
    const step =
      e.key === 'ArrowDown' || e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 0;
    if (!step || values.length === 0) return;
    e.preventDefault();
    const index = Math.max(0, values.indexOf(current));
    const next = values[(index + step + values.length) % values.length];
    select(next);
    document.getElementById(idOf(next))?.focus();
  };
}

/** What the plan includes, ticked; the mock allowance is already on the plan's own line. */
function PlanFeatures({ plan, cycle, offer, besideRadio }: { plan: PortalPlanItem; cycle: PortalCycle; offer: CycleOffer; besideRadio: boolean }) {
  const summary = planSummary(plan, offer);
  const features = periodFeatures(plan, cycle, offer).filter((feature) => feature !== summary);
  if (features.length === 0) return null;
  return (
    <ul className={cn('grid gap-x-5 gap-y-2 border-t border-border px-4 pb-4 pt-3.5 sm:grid-cols-2', besideRadio && 'sm:pl-[46px]')}>
      {features.map((feature) => (
        <li key={feature} className="flex items-start gap-2 text-[13px] leading-[18px] text-foreground/80">
          <Check className="mt-px h-3.5 w-3.5 shrink-0 text-success-600" strokeWidth={2.5} aria-hidden />
          <span className="min-w-0">{feature}</span>
        </li>
      ))}
    </ul>
  );
}

/** Name, recommended tag and summary on the left; the price, and what it was, on the right. */
function PlanHeading({
  plan,
  offer,
  showRecommended,
  isCurrentActive,
  isLowerThanActive,
  isCurrentRenewalDue,
  daysRemaining,
}: {
  plan: PortalPlanItem;
  offer: CycleOffer;
  showRecommended: boolean;
  isCurrentActive?: boolean;
  isLowerThanActive?: boolean;
  isCurrentRenewalDue?: boolean;
  daysRemaining?: number;
}) {
  return (
    <>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[15px] font-semibold leading-5 text-foreground">{planLabel(plan.name)}</span>
          {isCurrentActive ? (
            <Badge variant="outline" className="shrink-0 border-emerald-500/30 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
              Faol tarif
            </Badge>
          ) : isCurrentRenewalDue ? (
            <Badge className="shrink-0 bg-primary text-primary-foreground">
              Yangilash
            </Badge>
          ) : isLowerThanActive ? (
            <Badge variant="outline" className="shrink-0 text-muted-foreground opacity-70">
              Quyi tarif
            </Badge>
          ) : (
            showRecommended && plan.isRecommended && <Badge className="shrink-0">Tavsiya</Badge>
          )}
        </span>
        <span className="mt-0.5 block text-[13px] leading-[18px] text-muted-foreground">
          {isCurrentActive && typeof daysRemaining === 'number'
            ? `Amalda · ${daysRemaining} kun qoldi`
            : isLowerThanActive
            ? 'Amaldagi tarif tugagach mumkin'
            : isCurrentRenewalDue
            ? 'Muddati tugamoqda · Yangilash tavsiya etiladi'
            : planSummary(plan, offer)}
        </span>
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-[15px] font-semibold leading-5 tabular-nums text-foreground">
          {som(offer.price)}
        </span>
        {offer.originalPrice > offer.price && (
          <span className="mt-0.5 block text-[12.5px] leading-[18px] tabular-nums text-muted-foreground line-through">
            {som(offer.originalPrice)}
          </span>
        )}
      </span>
    </>
  );
}

export const PlanCheckoutModal: React.FC<PlanCheckoutModalProps> = ({
  open,
  onOpenChange,
  defaultPlan = 'PRO',
  onboardingOffer = false,
  defaultPaymentMethod = 'payme',
  subscription: subscriptionProp,
}) => {
  const { toast } = useToast();
  const [subscription, setSubscription] = useState<PortalSubscription | null>(subscriptionProp ?? null);
  const [plans, setPlans] = useState<PortalPlanItem[]>(PORTAL_PLANS);
  const [selectedPlanId, setSelectedPlanId] = useState<PlanId>(defaultPlan);
  const [chosenCycle, setChosenCycle] = useState<PortalCycle>('monthly');
  const [chosenMethod, setPaymentMethod] = useState<PaymentMethod>(defaultPaymentMethod);
  const [offeredMethods, setOfferedMethods] = useState<PaymentMethod[]>([]);
  const [methodsStatus, setMethodsStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [methodsAttempt, setMethodsAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<PortalPaymentDetails | null>(null);
  const [detailsStatus, setDetailsStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [detailsAttempt, setDetailsAttempt] = useState(0);
  const [paying, setPaying] = useState(false);
  const [paymeError, setPaymeError] = useState('');
  const checkoutRequest = useRef<AbortController | null>(null);

  // Keep subscription in sync or fetch when not provided as prop
  useEffect(() => {
    if (subscriptionProp !== undefined) {
      setSubscription(subscriptionProp);
      return;
    }
    if (!open) return;
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    const controller = new AbortController();
    fetch(`${config.api.base_url}/api/portal/billing/subscription`, {
      signal: controller.signal,
      cache: 'no-store',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!controller.signal.aborted && data && typeof data === 'object') {
          setSubscription(data as PortalSubscription);
        }
      })
      .catch(() => {
        // Guest or unauthenticated
      });
    return () => controller.abort();
  }, [open, subscriptionProp]);

  useEffect(() => {
    setPaying(false);
    setPaymeError('');
    if (open) {
      setPaymentMethod(defaultPaymentMethod);
      setChosenCycle('monthly');
    }
    return () => {
      checkoutRequest.current?.abort();
      checkoutRequest.current = null;
    };
  }, [open, defaultPaymentMethod]);

  useEffect(() => {
    setPaymentDetails(null);
    setDetailsStatus('loading');
    setCopied(false);
    setCopiedAmount(false);
    if (!open) return;
    const controller = new AbortController();
    getPortalPaymentDetails(controller.signal)
      .then(details => {
        if (controller.signal.aborted) return;
        setPaymentDetails(details);
        setDetailsStatus('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setDetailsStatus('error');
      });
    return () => controller.abort();
  }, [open, detailsAttempt]);

  // The methods platform admins switched on: the same list as the organization checkout.
  useEffect(() => {
    setMethodsStatus('loading');
    if (!open) return;
    const controller = new AbortController();
    getPortalPaymentMethods(controller.signal)
      .then((methods) => {
        if (controller.signal.aborted) return;
        setOfferedMethods(methods);
        setMethodsStatus('ready');
      })
      .catch(() => {
        if (!controller.signal.aborted) setMethodsStatus('error');
      });
    return () => controller.abort();
  }, [open, methodsAttempt]);

  // Fetch dynamic plans from API
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    fetch(`${config.api.base_url}/api/portal/plans`, { signal: controller.signal, cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
          const mapped: PortalPlanItem[] = data.data.map((p: any) => ({
            id: p.key as 'START' | 'PRO' | 'PREMIUM',
            name: p.name,
            originalPrice: p.monthlyPrice,
            discountPrice: p.discountPrice,
            dailyPrice: p.dailyPrice,
            mockLimit: p.mockLimit,
            analysisLimit: p.analysisLimit,
            quarterlyPrice: p.quarterlyPrice,
            weeklyPrice: p.weeklyPrice,
            weeklyAnalysisLimit: p.weeklyAnalysisLimit,
            subtitle: p.subtitle,
            isRecommended: p.isRecommended,
            features: buildPortalFeaturesList(p),
          }));
          if (!controller.signal.aborted) setPlans(mapped);
        }
      })
      .catch(() => {
        // Fallback to static seeds
      });
    return () => controller.abort();
  }, [open]);

  // The onboarding offer is one month of PRO; otherwise the learner picks a month, 3 months or a 7-day pass.
  const cycles = onboardingOffer ? (['monthly'] as PortalCycle[]) : offeredCycles(plans);
  const cycle: PortalCycle = cycles.includes(chosenCycle) ? chosenCycle : 'monthly';
  const cyclePlans = plans.filter((plan) => cycleOffer(plan, cycle) !== null);
  const currentPlan =
    cyclePlans.find((p) => p.id === (onboardingOffer ? 'PRO' : selectedPlanId)) || cyclePlans[0] || PORTAL_PLANS[1];
  const offer = cycleOffer(currentPlan, cycle) ?? cycleOffer(currentPlan, 'monthly')!;
  const cycleLabel = CYCLE_LABELS[cycle];
  const paymentMethod = pickPaymentMethod(offeredMethods, chosenMethod);
  const isOnline = paymentMethod !== null && isOnlineMethod(paymentMethod);
  const discountPercent =
    offer.originalPrice > 0
      ? Math.max(0, Math.round((1 - offer.price / offer.originalPrice) * 100))
      : 0;

  const activePlanKey = subscription?.planKey || (subscription?.planName ? subscription.planName.toUpperCase() : undefined);
  const isSubActive = Boolean(
    subscription &&
    subscription.isActive !== false &&
    new Date(subscription.periodEnd).getTime() > Date.now(),
  );
  const activeRank = isSubActive && activePlanKey ? getPlanRank(activePlanKey) : 0;
  const isRenewalDue = Boolean(
    subscription?.isRenewalDue ||
    (subscription && isSubActive && (new Date(subscription.periodEnd).getTime() - Date.now() <= 3 * 24 * 60 * 60 * 1000)) ||
    (subscription && subscription.isExpired),
  );

  const selectedRank = getPlanRank(currentPlan.id);
  const isCurrentActivePlanSelected = isSubActive && currentPlan.id === activePlanKey && !isRenewalDue;
  const isLowerPlanSelected = isSubActive && activeRank > 0 && selectedRank < activeRank;
  const isRenewalDuePlanSelected = isSubActive && currentPlan.id === activePlanKey && isRenewalDue;
  const isUpgradeSelected = isSubActive && activeRank > 0 && selectedRank > activeRank;

  const handleOnlineCheckout = async () => {
    if (checkoutRequest.current || !paymentMethod || !isOnlineMethod(paymentMethod)) return;
    if (isCurrentActivePlanSelected) {
      setPaymeError("Sizda ushbu tarif allaqachon faol. Tarif muddati tugashiga 3 kun qolganda yoki tugagach yangilashingiz mumkin.");
      return;
    }
    if (isLowerPlanSelected) {
      setPaymeError("Sizda yuqoriroq tarif faol. Amaldagi tarif tugagunga qadar quyi tarifga o‘tib bo‘lmaydi.");
      return;
    }
    const controller = new AbortController();
    checkoutRequest.current = controller;
    setPaying(true);
    setPaymeError('');
    try {
      const checkout = await createPortalCheckout(currentPlan.id, offer.price, paymentMethod, controller.signal, cycle);
      if (!controller.signal.aborted) window.location.assign(checkout.checkoutUrl);
    } catch (error: unknown) {
      if (!controller.signal.aborted) {
        const message = (error as { response?: { data?: { message?: unknown } } }).response?.data?.message;
        setPaymeError(typeof message === 'string' ? message : 'To‘lov sahifasini ochib bo‘lmadi. Qayta urinib ko‘ring.');
      }
    } finally {
      if (checkoutRequest.current === controller) {
        checkoutRequest.current = null;
        setPaying(false);
      }
    }
  };

  useEffect(() => {
    if (defaultPlan) setSelectedPlanId(defaultPlan);
  }, [defaultPlan]);

  // When opening, if user has an active subscription, smart-select renewal or eligible upgrade
  useEffect(() => {
    if (!open || !isSubActive || onboardingOffer) return;
    setSelectedPlanId((currentSelected) => {
      if (isRenewalDue && activePlanKey) {
        const matching = plans.find((p) => p.id === activePlanKey);
        if (matching) return matching.id;
      } else if (currentSelected === activePlanKey || getPlanRank(currentSelected) < activeRank) {
        const upgrade = plans.find((p) => getPlanRank(p.id) > activeRank);
        if (upgrade) return upgrade.id;
      }
      return currentSelected;
    });
  }, [open, isSubActive, isRenewalDue, activePlanKey, activeRank, onboardingOffer, plans]);

  const handleCopyCard = async () => {
    if (!paymentDetails || detailsStatus !== 'ready') return;
    try {
      await navigator.clipboard.writeText(paymentDetails.cardNumber);
      setCopied(true);
      toast({
        title: 'Nusxalandi!',
        description: 'Karta raqami buferga nusxalandi.',
      });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({
        title: 'Xatolik',
        description: 'Karta raqamini nusxalab bo‘lmadi. Qo‘lda ko‘chirib oling.',
        variant: 'destructive',
      });
    }
  };

  const handleCopyAmount = async () => {
    try {
      await navigator.clipboard.writeText(String(offer.price));
      setCopiedAmount(true);
      toast({
        title: 'Nusxalandi!',
        description: 'To‘lov summasi buferga nusxalandi.',
      });
      setTimeout(() => setCopiedAmount(false), 2500);
    } catch {
      toast({
        title: 'Xatolik',
        description: 'Summani nusxalab bo‘lmadi. Qo‘lda ko‘chirib oling.',
        variant: 'destructive',
      });
    }
  };

  const telegramUrl = React.useMemo(() => {
    const text = encodeURIComponent(
      `Assalomu alaykum! Examing platformasida ${cycleLabel.adjective} ${currentPlan.id} tarif (${offer.price.toLocaleString()} so'm) uchun to'lov qildim. Chekni ilova qilaman.`,
    );
    return `${PAYMENT_SUPPORT_TELEGRAM}?text=${text}`;
  }, [currentPlan.id, offer.price, cycleLabel]);

  const planIds = cyclePlans.map((plan) => plan.id);
  // A period some plans are not sold for (the 7-day pass) moves the choice to one that is.
  const chooseCycle = (next: PortalCycle) => {
    setChosenCycle(next);
    const sold = plans.filter((plan) => cycleOffer(plan, next) !== null);
    if (!sold.some((plan) => plan.id === selectedPlanId)) {
      const pick = sold.find((plan) => plan.isRecommended) ?? sold[0];
      if (pick) setSelectedPlanId(pick.id);
    }
  };
  const cardDetailsRef = useRef<HTMLDivElement>(null);
  // Picking the card transfer brings its details into view: on a phone they open below the fold.
  const choosePaymentMethod = (method: PaymentMethod) => {
    const switchingToCard = method === 'card' && paymentMethod !== 'card';
    setPaymentMethod(method);
    if (switchingToCard) {
      requestAnimationFrame(() => cardDetailsRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }));
    }
  };
  const cardReady = detailsStatus === 'ready' && paymentDetails !== null;
  const title = onboardingOffer ? `${planLabel(currentPlan.name)} obunasi` : 'Tarifni tanlang';
  const validity = cycle === 'weekly' ? 'Pass 7 kun amal qiladi.' : `Obuna ${cycleLabel.period} amal qiladi.`;
  const description = onboardingOffer
    ? `Onboarding bonusi: ${discountPercent}% chegirma. ${validity}`
    : validity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        closeClassName="hidden"
        className="flex max-h-[calc(100dvh-24px)] w-[calc(100vw-24px)] max-w-[520px] flex-col gap-0 overflow-hidden rounded-xl p-0"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-4 sm:px-6 sm:py-5">
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold leading-6 tracking-[-0.01em] text-foreground">{title}</DialogTitle>
            <DialogDescription className="mt-1 text-[13.5px] leading-5 text-muted-foreground">
              {description}
            </DialogDescription>
          </div>
          <button
            type="button"
            aria-label="Yopish"
            onClick={() => onOpenChange(false)}
            className="-mr-2 -mt-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-[18px] w-[18px]" aria-hidden />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-6">
          {/* Plan */}
          <section aria-labelledby="plan-heading">
            <h3 id="plan-heading" className="mb-2.5 text-[13px] font-semibold text-foreground">
              Tarif
            </h3>
            {cycles.length > 1 && (
              <div
                role="radiogroup"
                aria-label="Muddat"
                className="mb-3 grid grid-cols-3 gap-1 rounded-lg bg-muted p-1"
                onKeyDown={arrowKeys<PortalCycle>(cycles, cycle, chooseCycle, (id) => `cycle-option-${id}`)}
              >
                {cycles.map((option) => {
                  const isSelected = option === cycle;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="radio"
                      id={`cycle-option-${option}`}
                      aria-checked={isSelected}
                      tabIndex={isSelected ? 0 : -1}
                      disabled={paying}
                      onClick={() => chooseCycle(option)}
                      className={cn(
                        'h-8 rounded-md text-[13px] font-semibold transition-colors',
                        CHOICE_FOCUS,
                        isSelected ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {CYCLE_LABELS[option].tab}
                    </button>
                  );
                })}
              </div>
            )}
            {onboardingOffer ? (
              <div className={cn(CHOICE, CHOICE_ON)}>
                <div className="flex items-start gap-3 px-4 py-3.5">
                  <PlanHeading plan={currentPlan} offer={offer} showRecommended={false} />
                </div>
                <PlanFeatures plan={currentPlan} cycle={cycle} offer={offer} besideRadio={false} />
              </div>
            ) : (
              <div
                role="radiogroup"
                aria-label="Tarif rejasini tanlash"
                className="flex flex-col gap-2.5"
                onKeyDown={arrowKeys<PlanId>(planIds, currentPlan.id, (id) => setSelectedPlanId(id), (id) => `plan-option-${id}`)}
              >
                {cyclePlans.map((plan) => {
                  const planOffer = cycleOffer(plan, cycle)!;
                  const isSelected = plan.id === currentPlan.id;
                  const isCurrentActive = isSubActive && plan.id === activePlanKey && !isRenewalDue;
                  const isLowerThanActive = isSubActive && activeRank > 0 && getPlanRank(plan.id) < activeRank;
                  const isCurrentRenewalDue = isSubActive && plan.id === activePlanKey && isRenewalDue;
                  const isDisabled = paying || isCurrentActive || isLowerThanActive;
                  return (
                    <div
                      key={plan.id}
                      className={cn(
                        CHOICE,
                        isSelected ? CHOICE_ON : CHOICE_OFF,
                        (isCurrentActive || isLowerThanActive) && 'opacity-65 bg-muted/20 border-dashed',
                      )}
                    >
                      <button
                        type="button"
                        role="radio"
                        id={`plan-option-${plan.id}`}
                        aria-checked={isSelected}
                        tabIndex={isSelected ? 0 : -1}
                        onClick={() => {
                          if (!isDisabled) setSelectedPlanId(plan.id);
                        }}
                        disabled={isDisabled}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-lg px-4 py-3.5 text-left',
                          CHOICE_FOCUS,
                          isDisabled && 'cursor-not-allowed',
                        )}
                      >
                        <RadioMark checked={isSelected} />
                        <PlanHeading
                          plan={plan}
                          offer={planOffer}
                          showRecommended={!isCurrentActive && !isLowerThanActive}
                          isCurrentActive={isCurrentActive}
                          isLowerThanActive={isLowerThanActive}
                          isCurrentRenewalDue={isCurrentRenewalDue}
                          daysRemaining={subscription?.daysRemaining}
                        />
                      </button>
                      {isSelected && <PlanFeatures plan={plan} cycle={cycle} offer={planOffer} besideRadio />}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Payment method */}
          <section aria-labelledby="payment-heading">
            <h3 id="payment-heading" className="mb-2.5 text-[13px] font-semibold text-foreground">
              To'lov usuli
            </h3>
            {methodsStatus === 'loading' ? (
              <p role="status" className="flex items-center gap-2 py-3 text-[13.5px] text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                To‘lov usullari yuklanmoqda…
              </p>
            ) : methodsStatus === 'error' ? (
              <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-background p-4">
                <p role="alert" className="text-[13.5px] text-foreground">
                  To‘lov usullarini yuklab bo‘lmadi. Qayta urinib ko‘ring.
                </p>
                <Button variant="outline" size="xs" onClick={() => setMethodsAttempt((attempt) => attempt + 1)}>
                  Qayta urinish
                </Button>
              </div>
            ) : offeredMethods.length === 0 || !paymentMethod ? (
              <p role="alert" className="rounded-lg border border-border bg-background p-4 text-[13.5px] text-muted-foreground">
                To‘lov hozircha mavjud emas. Keyinroq urinib ko‘ring.
              </p>
            ) : (
              <>
              <div
                role="radiogroup"
                aria-label="To'lov usuli"
                className="grid grid-cols-1 gap-2.5 sm:grid-cols-2"
                onKeyDown={arrowKeys<PaymentMethod>(
                  offeredMethods,
                  paymentMethod,
                  choosePaymentMethod,
                  (id) => `payment-option-${id}`,
                )}
              >
                {offeredMethods.map((method) => {
                  const isSelected = method === paymentMethod;
                  return (
                    <button
                      key={method}
                      type="button"
                      role="radio"
                      id={`payment-option-${method}`}
                      aria-checked={isSelected}
                      tabIndex={isSelected ? 0 : -1}
                      onClick={() => choosePaymentMethod(method)}
                      disabled={paying}
                      className={cn(
                        CHOICE,
                        CHOICE_FOCUS,
                        'flex items-start gap-3 px-3.5 py-3 text-left',
                        isSelected ? CHOICE_ON : CHOICE_OFF,
                      )}
                    >
                      <RadioMark checked={isSelected} />
                      <span className="min-w-0">
                        <PaymentMethodMark
                          method={method}
                          cardLabel={CARD_TRANSFER_LABEL}
                          className="text-sm font-semibold leading-5 text-foreground"
                        />
                        <span className="mt-0.5 block text-[12.5px] leading-[17px] text-muted-foreground">{methodHint(method)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {isOnline ? (
                <p className="mt-3 text-[13px] leading-5 text-muted-foreground">
                  To'lov {METHOD_NAMES[paymentMethod]} sahifasida amalga oshiriladi.
                </p>
              ) : (
                <div ref={cardDetailsRef} className="mt-4 scroll-mb-4">
                  {detailsStatus === 'loading' ? (
                    <p role="status" className="flex items-center gap-2 py-3 text-[13.5px] text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      To‘lov rekvizitlari yuklanmoqda…
                    </p>
                  ) : detailsStatus === 'error' ? (
                    <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-background p-4">
                      <p role="alert" className="text-[13.5px] text-foreground">
                        To‘lov rekvizitlarini yuklab bo‘lmadi. Qayta urinib ko‘ring.
                      </p>
                      <Button variant="outline" size="xs" onClick={() => setDetailsAttempt((attempt) => attempt + 1)}>
                        Qayta urinish
                      </Button>
                    </div>
                  ) : !paymentDetails ? (
                    <p role="alert" className="rounded-lg border border-border bg-background p-4 text-[13.5px] text-muted-foreground">
                      Karta orqali to‘lov hozircha mavjud emas. Keyinroq urinib ko‘ring.
                    </p>
                  ) : (
                    <>
                      <div className="rounded-lg border border-border">
                        <div className="flex items-center justify-between gap-3 px-4 py-3">
                          <div className="min-w-0">
                            <p className="text-[12.5px] leading-[17px] text-muted-foreground">Karta raqami</p>
                            <p className="mt-0.5 font-mono text-[15px] font-semibold tracking-[0.04em] text-foreground">
                              {paymentDetails.cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ')}
                            </p>
                          </div>
                          <Button type="button" variant="outline" size="xs" onClick={handleCopyCard} className="shrink-0 gap-1.5">
                            {copied ? (
                              <Check className="h-3.5 w-3.5 text-success-600" strokeWidth={2.5} aria-hidden />
                            ) : (
                              <Copy className="h-3.5 w-3.5" aria-hidden />
                            )}
                            {copied ? 'Nusxalandi' : 'Nusxalash'}
                          </Button>
                        </div>
                        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 border-t border-border px-4 py-2.5 text-[13.5px]">
                          <dt className="py-1 text-muted-foreground">Qabul qiluvchi</dt>
                          <dd className="truncate py-1 text-right font-medium text-foreground">{paymentDetails.cardHolderName}</dd>
                          <dt className="py-1 text-muted-foreground">Summa</dt>
                          <dd className="flex items-center justify-end gap-1 py-1 font-semibold tabular-nums text-foreground">
                            {som(offer.price)}
                            <button
                              type="button"
                              onClick={handleCopyAmount}
                              aria-label="Summani nusxalash"
                              className="-my-1 -mr-1.5 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              {copiedAmount ? (
                                <Check className="h-3.5 w-3.5 text-success-600" strokeWidth={2.5} aria-hidden />
                              ) : (
                                <Copy className="h-3.5 w-3.5" aria-hidden />
                              )}
                            </button>
                          </dd>
                        </dl>
                      </div>
                      <ol className="mt-3.5 space-y-2 text-[13.5px] leading-5 text-foreground/85">
                        <li className="flex gap-2.5">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11.5px] font-semibold text-foreground">
                            1
                          </span>
                          <span>Kartaga {som(offer.price)} o'tkazing.</span>
                        </li>
                        <li className="flex gap-2.5">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[11.5px] font-semibold text-foreground">
                            2
                          </span>
                          <span>To'lov chekini Telegram orqali yuboring. Tarif 2–5 daqiqada faollashadi.</span>
                        </li>
                      </ol>
                    </>
                  )}
                </div>
              )}
              </>
            )}
          </section>
        </div>

        {/* Total and the one lime action */}
        <footer className="shrink-0 border-t border-border bg-card px-5 py-4 sm:px-6">
          {isOnline && paymeError && (
            <p
              role="alert"
              className="mb-3 rounded-md border border-error-200 bg-error-50 px-3 py-2 text-[13px] text-error-700 dark:border-error-900/50 dark:bg-error-900/20 dark:text-error-400"
            >
              {paymeError}
            </p>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="flex items-baseline justify-between gap-3 sm:block">
              <p className="whitespace-nowrap text-xl font-bold leading-7 tracking-[-0.01em] tabular-nums text-foreground">
                {som(offer.price)}
              </p>
              <p className="text-right text-[12.5px] leading-[17px] text-muted-foreground sm:text-left">
                <span className="whitespace-nowrap">{cycleLabel.period}</span> ·{' '}
                <span className="whitespace-nowrap">kuniga ~{som(offer.perDay)}</span>
              </p>
            </div>
            {!paymentMethod ? null : isOnline ? (
              <Button
                type="button"
                onClick={handleOnlineCheckout}
                disabled={paying || isCurrentActivePlanSelected || isLowerPlanSelected}
                aria-busy={paying}
                className="h-11 w-full gap-2 px-6 text-[15px] font-semibold sm:w-auto"
              >
                {paying && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
                {paying
                  ? 'To‘lov sahifasi ochilmoqda…'
                  : isCurrentActivePlanSelected
                  ? 'Tarif allaqachon faol'
                  : isLowerPlanSelected
                  ? 'Quyi tarifga o‘tib bo‘lmaydi'
                  : isRenewalDuePlanSelected
                  ? 'Tarifni yangilash'
                  : isUpgradeSelected
                  ? 'Yuqori tarifga o‘tish'
                  : `${METHOD_NAMES[paymentMethod]} orqali to'lash`}
              </Button>
            ) : (
              cardReady && (
                isCurrentActivePlanSelected || isLowerPlanSelected ? (
                  <Button disabled className="h-11 w-full gap-2 px-6 text-[15px] font-semibold sm:w-auto">
                    {isCurrentActivePlanSelected ? 'Tarif allaqachon faol' : 'Quyi tarifga o‘tib bo‘lmaydi'}
                  </Button>
                ) : (
                  <Button asChild className="h-11 w-full gap-2 px-6 text-[15px] font-semibold sm:w-auto">
                    <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
                      <Send className="h-4 w-4" aria-hidden />
                      Chekni Telegramga yuborish
                    </a>
                  </Button>
                )
              )
            )}
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
};

export default PlanCheckoutModal;
